/**
 * ShiftCreateDialog Component
 * Modal dialog for creating new shifts from the calendar view.
 * Admin can specify walkbook, date/time, and duration.
 */

import { useState, useMemo } from 'react';
import { Calendar, Home, Loader2, UserPlus } from 'lucide-react';
import type { CreateShiftRequest } from '@signalvote/shared';
import { CanvasserType } from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCreateShift, useWalkbooks, useVolunteers, useVendors } from '@/hooks/queries';
import { readyStaff, staffUnreadyMessage } from '@/lib/staffRead';

// ============================================================================
// Types
// ============================================================================

interface ShiftCreateDialogProps {
  open: boolean;
  onClose: () => void;
  initialDate: string;  // YYYY-MM-DD format
  initialHour: number;  // 0-23
}

// ============================================================================
// Component
// ============================================================================

export function ShiftCreateDialog({
  open,
  onClose,
  initialDate,
  initialHour,
}: ShiftCreateDialogProps) {
  const { mutate: createShift, isPending: isCreating } = useCreateShift();
  const walkbooksRead = useWalkbooks(undefined);
  const walkbooks = walkbooksRead.status === 'ready' ? walkbooksRead.data : undefined;
  const walkbooksLoading = walkbooksRead.status === 'loading' || walkbooksRead.status === 'idle';
  const staffRead = useVolunteers(undefined);
  const volunteers = readyStaff(staffRead);
  const volunteersLoading = staffRead.status === 'loading' || staffRead.status === 'idle';
  const staffProblem = staffUnreadyMessage(staffRead);
  const { data: vendors } = useVendors();

  // Form state
  const [name, setName] = useState('');
  const [selectedWalkbookId, setSelectedWalkbookId] = useState<string>('');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('');
  const [date, setDate] = useState(initialDate);
  const [startTime, setStartTime] = useState(
    `${initialHour.toString().padStart(2, '0')}:00`
  );
  const [estimatedHours, setEstimatedHours] = useState(2);

  // Canvasser type/vendor filter state
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [vendorFilter, setVendorFilter] = useState<string>('');

  // Assign-to lists every active campaign user, not only formal canvassers — a member
  // with a permission preset (e.g. Volunteer) but no CanvasserType is still assignable.
  // The type/vendor filters below narrow the list when the admin wants only canvassers.
  const activeVolunteers = useMemo(() => {
    let list = volunteers?.filter((v) => v.isActive) ?? [];
    if (typeFilter) {
      list = list.filter((v) => v.canvasserType === typeFilter);
    }
    if (vendorFilter) {
      list = list.filter((v) => v.vendorId === vendorFilter);
    }
    return list;
  }, [volunteers, typeFilter, vendorFilter]);

  const selectedWalkbook = walkbooks?.find((w) => w.id === selectedWalkbookId) || null;

  const handleCreate = () => {
    if (!selectedWalkbook || !name.trim()) return;

    const request: CreateShiftRequest = {
      name: name.trim(),
      date,
      startTime,
      estimatedMinutes: Math.round(estimatedHours * 60),
      walkbookId: selectedWalkbook.id,
      assignedToId: selectedAssigneeId || null,
    };

    createShift(request, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const isValid = name.trim() && selectedWalkbook && date && startTime && estimatedHours > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Create New Shift
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
          {/* Shift Name - Full Width */}
          <div className="col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Shift Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Morning Canvass - Downtown"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Walkbook Selection - Full Width */}
          <div className="col-span-2">
            <label htmlFor="walkbook" className="block text-sm font-medium text-gray-700 mb-1.5">
              Walkbook <span className="text-red-500">*</span>
            </label>
            {walkbooksLoading ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading walkbooks...
              </div>
            ) : (
              <select
                id="walkbook"
                value={selectedWalkbookId}
                onChange={(e) => setSelectedWalkbookId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select a walkbook...</option>
                {walkbooks?.map((walkbook) => (
                  <option key={walkbook.id} value={walkbook.id}>
                    {walkbook.name} ({walkbook.houseCount} houses)
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Walkbook Info Display */}
          {selectedWalkbook && (
            <div className="col-span-2 flex gap-4 p-3 bg-muted/50 rounded-md">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Home className="h-4 w-4" />
                {selectedWalkbook.houseCount} houses, {selectedWalkbook.voterCount} voters
              </div>
            </div>
          )}

          {/* Canvasser Type / Vendor Filters */}
          <div>
            <label htmlFor="canvasserType" className="block text-sm font-medium text-gray-700 mb-1.5">
              Canvasser Type
            </label>
            <select
              id="canvasserType"
              value={typeFilter}
              onChange={(e) => {
                const newType = e.target.value;
                setTypeFilter(newType);
                // Clear vendor filter when switching to Volunteer
                if (newType === CanvasserType.Volunteer) {
                  setVendorFilter('');
                }
                // Reset assignee if current selection would be filtered out
                if (selectedAssigneeId) {
                  const stillVisible = (volunteers?.filter((v) => v.isActive) ?? []).some((v) => {
                    if (newType && v.canvasserType !== newType) return false;
                    return v.userId === selectedAssigneeId;
                  });
                  if (!stillVisible) setSelectedAssigneeId('');
                }
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="">All Types</option>
              <option value={CanvasserType.Volunteer}>Volunteer</option>
              <option value={CanvasserType.Paid}>Paid</option>
            </select>
          </div>
          {typeFilter !== CanvasserType.Volunteer && (
            <div>
              <label htmlFor="vendorFilter" className="block text-sm font-medium text-gray-700 mb-1.5">
                Vendor
              </label>
              <select
                id="vendorFilter"
                value={vendorFilter}
                onChange={(e) => {
                  setVendorFilter(e.target.value);
                  // Reset assignee if current selection would be filtered out
                  if (selectedAssigneeId && e.target.value) {
                    const vol = volunteers?.find((v) => v.userId === selectedAssigneeId);
                    if (vol && vol.vendorId !== e.target.value) setSelectedAssigneeId('');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">All Vendors</option>
                {(vendors ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Assign To (optional) */}
          <div className="col-span-2">
            <label htmlFor="assignee" className="block text-sm font-medium text-gray-700 mb-1.5">
              <UserPlus className="inline h-4 w-4 mr-1" />
              Assign To
            </label>
            {staffProblem ? (
              <p role="status" className="px-3 py-2 text-sm text-muted-foreground">
                {staffProblem}
              </p>
            ) : volunteersLoading ? (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading canvassers...
              </div>
            ) : (
              <select
                id="assignee"
                value={selectedAssigneeId}
                onChange={(e) => setSelectedAssigneeId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">No one (available for claiming)</option>
                {activeVolunteers.map((vol) => {
                  const type = vol.canvasserType ?? '';
                  const suffix = type
                    ? vol.vendorName
                      ? `(${type} - ${vol.vendorName})`
                      : `(${type})`
                    : '';
                  return (
                    <option key={vol.userId} value={vol.userId}>
                      {vol.firstName} {vol.lastName} {suffix}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          {/* Date */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1.5">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Start Time */}
          <div>
            <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1.5">
              Start Time <span className="text-red-500">*</span>
            </label>
            <input
              id="startTime"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Duration */}
          <div className="col-span-2 sm:col-span-1">
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1.5">
              Duration <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="duration"
                type="number"
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(Number(e.target.value))}
                min={0.5}
                max={8}
                step={0.5}
                className="w-full px-3 py-2 pr-14 border border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                hours
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!isValid || isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Shift'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShiftCreateDialog;
